import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { FieldWrapper, FormlyFieldConfig } from '@ngx-formly/core';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'mutara-wrapper-group',
  templateUrl: './group-wrapper.component.html',
  styleUrls: ['./group-wrapper.component.scss'],
  standalone: false
})
export class GroupWrapperComponent extends FieldWrapper implements OnInit {

  private firstSkip: boolean = true;
  isAnchor: boolean = false;
  needToPick: boolean = false;
  selected: boolean = false;
  childFields: FormlyFieldConfig[] | undefined;
  searchItems: string[] = [];
  keyList: string[] | undefined;
  /* WARNING - this only gets the FIRST menu from the template */
  @ViewChild(MatAutocompleteTrigger) trigger: MatAutocompleteTrigger | undefined;

  constructor(public http: HttpClient) {
    super();
  }

  get debug() : boolean {
    return this.props?.capForm?.debug;
  }

  ngOnInit() {
    this.isAnchor = this.props.placeholder?.startsWith('http') ?? false;
    // If the subset of values is a search, pipe the changes to the service 
    this.childFields = this.field.fieldGroup;

    if (!!this.props.searchList) {
      this.keyList = Object.keys(this.props.searchList.verify.map); // .map(key => this.props.searchList.verify.map[key]);

      this.props.blur = (field) => {
        if (this.keyList?.includes(<string>field.key)) {
          this.validationSearch(field.model);
        }
      }
      this.formControl.valueChanges.pipe(
        debounceTime(700),
        distinctUntilChanged(),
        map((val: any) => {
          let srcField;
          this.keyList?.forEach(key => {
            let fld = this.field.fieldGroup?.find(child => child.key == key);
            if (fld?.focus) {
              srcField = fld;
            }
          });
          if (!!this.firstSkip || !srcField) {
            this.firstSkip = false;
            return null;
          }
          return { search: this.searchText, focus: srcField };
        }),
      ).subscribe({
        error: (err) => {
          console.error(err);
        },
        next: (searchData: any) => {
          if (!!searchData?.search) {
            if ( this.debug ) { console.log ( 'VC group %O Search for %O', this.field.key, searchData.search ); }
            this.http.post<string[]>(this.props.searchList.search, searchData.search).subscribe({
              error: (err) => {
                console.error(err);
              },
              next: (result: string[]) => {
                if (!!result && result?.length > 1) {
                  this.searchItems = result;
                  this.trigger?.openPanel();
                } else if (!!result && result?.length == 1) {
                  this.validationSearch(result[0]);
                } else {
                  this.searchItems = [];
                  this.trigger?.closePanel();
                }
              }
            });
          }
        }
      });
    } else {
      this.searchItems = [];
      this.trigger?.closePanel();
    }
  }

  /** Take the sub-fields from the map and map them to output fields */
  get searchText(): any {
    const dataModel : any = this.field.model, newText : any = {};
    // flatten the data 
    Object.keys(dataModel).forEach((key) => {
      let fullKey = this.verify.map[key];
      if (typeof dataModel[key] !== 'object' || !dataModel[key]) {
        if (!!fullKey) {
          newText[fullKey] = dataModel[key];
        }
      } else {
        let topLevel = dataModel[key];
        Object.keys(topLevel).forEach(lower => {
          fullKey = this.verify.map[key + '.' + lower];
          if (!!fullKey) {
            newText[fullKey] = topLevel[lower];
          }
        });
      }
    }, {});

    return newText;
  }
  /** Figure out if we need to hide the title (no children) */
  get visibleChildren() {
    return this?.childFields?.some(field => !field.hide);
  }

  get verify() {
    return this.props?.searchList?.verify;
  }

  get countryCode(): string | undefined {
    let searchObject = Object.keys(this.searchText).find((key) => key.includes('country'));
    return searchObject ? this.searchText[searchObject] : undefined;
  }

  isValid(field: FormlyFieldConfig): boolean {
    if (field.key && !!field.formControl) {
      return field.formControl.valid;
    }

    return field.fieldGroup ? field.fieldGroup.every((f) => this.isValid(f)) : true;
  }

  resetGroup() {
    this.field.fieldGroup?.forEach( (f : FormlyFieldConfig) => {
      f.formControl?.reset();
    });
  }

  /** Validate the searchText and apply to current value */
  validationSearch(searchText : string) {
    
    if (!!this.props.searchList?.verify) {
      this.http.post<any>(this.verify.url, { search: searchText, country_code: this.countryCode ?? 'USA' }).subscribe({
        error: (err) => {
          console.error(err);
        },
        next: (result: any) => {
          if (!!result) {
            //console.log('validated address %O', result)
            const mapToRecord : any = this.verify.map, mapIfHide : any = this.verify.hideMap;
            for (const [common, mapped] of Object.entries(mapToRecord)) {
              let toMap : FormlyFieldConfig | undefined = this.childFields?.find((fld : FormlyFieldConfig) => fld.key == common);
              if (!!toMap) {
                try {
                  if ( toMap.hide ) {
                    const mapField = mapIfHide[<string>mapped];
                    if ( mapField ) {
                      toMap?.props?.setValue(null, true);
                      toMap = this.childFields?.find( (fld : FormlyFieldConfig) => fld.key == mapField); 
                    } 
                  }
                  //console.log('set address value %O -> %O',toMap.key, result[map]);
                  toMap?.props?.setValue( result[<string>mapped], true ); //form.controls[<string>toMap.key].setValue(result[<string>mapped]);
                } catch (err) {
                  console.error('Error: Set of value %O failed\n%O', toMap?.key, err);
                }
              }
            };
            this.firstSkip = true;
          }
        }
      });
    }
  }

  /** Called from the template to select the menu item */
  selectSearchItem(index: number) {
    this.trigger?.closePanel();
    this.validationSearch(this.searchItems[index]);
  }
}

@Component({
  selector: 'mutara-column-group',
  template: `
  <div class='mutara-column'>
    <ng-container #fieldComponent></ng-container>
  </div>`,
  styleUrls: ['./group-wrapper.component.scss'],
  standalone: false
})
export class ColumnWrapperComponent extends GroupWrapperComponent {
}
@Component({
  selector: 'mutara-column-footer',
  template: `
  <mat-card-actions [align]="props?.align ?? 'end'">
    <ng-container #fieldComponent></ng-container>
  </mat-card-actions>`,
  styleUrls: ['./group-wrapper.component.scss'],
  standalone: false
})
export class FooterWrapperComponent extends GroupWrapperComponent {
}